import React, { useState } from 'react';
import SwipeableModal from '../../../components/SwipeableModal';
import Icon from '../../../components/AppIcon';

const VideoTutorialsModal = ({ isOpen, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const tutorials = [
  {
    id: 1,
    title: 'Getting Started with MyBodyToday',
    description: 'Welcome to MyBodyToday! This tutorial covers account setup, profile creation, selecting your user type (General Adult, Elderly, Long-term Condition, Child/Teen, or Caregiver), and navigating the main dashboard. Learn how to customize your experience and understand the app layout.',
    duration: '5:30',
    category: 'Basics',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1977a7f14-1769600618145.png",
    videoUrl: '#',
    prompt: `Create a 5-minute tutorial video for "Getting Started with MyBodyToday" health tracking app. Show: 1. Opening screen with MyBodyToday logo and tagline"Track Your Health Journey"
2. Sign-up process: email, password, and profile creation form
3. User type selection screen with 5 cards:
   - General Adult (active lifestyle icon)
   - Elderly (senior care icon)
   - Long-term Condition (medical chart icon)
   - Child/Teen (youth icon)
   - Caregiver (helping hands icon)
4. Main dashboard tour:
   - Top navigation with user profile in top-right corner
   - Daily Check-In button (prominent, center)
   - Symptom History timeline
   - Quick stats cards (entries this week, severity trends)
   - Bottom navigation tabs
5. Settings overview: accessibility options, notifications, privacy controls
6. Closing: "You're ready to start tracking!"

Style: Clean, modern UI with calming blue/green color scheme. Use screen recordings with voiceover narration. Include on-screen text highlights for key features.`
  },
  {
    id: 2,
    title: 'Recording Your First Daily Check-In',
    description: 'Learn how to log your daily symptoms using the comprehensive check-in system. This tutorial demonstrates selecting symptom categories, rating severity levels (1-10), choosing timing (morning/afternoon/evening), mapping body locations, tracking triggers, recording medications, and adding notes or voice input for detailed tracking.',
    duration: '8:15',
    category: 'Daily Check-In',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecbb8d11-1771908843314.png",
    videoUrl: '#',
    prompt: `Create an 8-minute detailed tutorial for "Daily Check-In" in MyBodyToday app. Demonstrate: 1. Tap"Daily Check-In" button from dashboard
2. Feeling Level Selector:
   - Show 4 emoji options: Good (😊), Mild (😐), Moderate (😟), Severe (😣)
   - User selects "Moderate"
3. Symptom Category Selector:
   - Display grid of categories: Head & Neck, Respiratory, Digestive, Musculoskeletal, Skin, Cardiovascular, Neurological, Other
   - User selects "Head & Neck" and "Musculoskeletal"
4. Severity Slider:
   - Interactive 1-10 scale with color gradient (green to red)
   - User drags to 6/10
5. Timing Selector:
   - Three buttons: Morning, Afternoon, Evening
   - User selects "Morning"
6. Body Location Mapper:
   - Interactive body diagram (front/back views)
   - User taps head and lower back areas
7. Daily Impact Assessment:
   - Checkboxes: Work, Sleep, Mobility, Social Activities, Exercise
   - User checks "Sleep" and "Exercise"
8. Trigger Selector:
   - Common triggers: Stress, Weather, Food, Activity, Sleep, Medication
   - User selects "Stress" and "Weather"
9. Medication Tracker:
   - "Did you take your medications today?" with Yes/No/Partially options
   - Text field for medication details
10. Notes & Voice Input:
    - Text area for written notes
    - Microphone button for voice recording
    - User adds note: "Headache started after morning meeting"
11. Photo Attachment (optional):
    - Camera icon to attach symptom photos
12. Review & Submit:
    - Summary card showing all entered data
    - "Submit Check-In" button
    - Success confirmation with confetti animation

Style: Step-by-step walkthrough with zoom-ins on each feature. Use annotations and arrows to guide attention. Include voiceover explaining each step's purpose.`
  },
  {
    id: 3,
    title: 'Understanding Your Symptom History', description: 'Master the Symptom History page with timeline and calendar views. Learn to filter by date range, symptom type, and severity. Discover how to identify patterns in your health data, view detailed entry information, and use the calendar heatmap to spot trends over weeks and months.', duration: '6:45', category: 'History & Analytics',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_126f44da0-1767458874136.png",
    videoUrl: '#',
    prompt: `Create a 7-minute tutorial for "Symptom History" feature in MyBodyToday. Show: 1. Navigate to"Symptom History" from bottom navigation
2. Timeline View:
   - Vertical timeline with date markers
   - Each entry shows: date, time, severity badge (color-coded), symptom icons
   - Tap entry to expand: full details, body location map, triggers, notes
3. Calendar View:
   - Monthly calendar with color-coded dots on dates with entries
   - Color legend: Green (mild), Yellow (moderate), Red (severe)
   - Heatmap intensity shows frequency of entries
   - Tap date to see all entries for that day
4. Filter Controls:
   - Date range picker: Last 7 days, Last 30 days, Last 3 months, Custom
   - Symptom category filter: Multi-select dropdown
   - Severity filter: Slider to show only entries above certain severity
   - Apply filters and watch timeline update in real-time
5. Pattern Recognition:
   - Highlight recurring symptoms on same days of week
   - Show severity trend graph over time
   - Identify trigger correlations (e.g., "Stress appears in 70% of severe entries")
6. Entry Details Modal:
   - Full symptom breakdown
   - Body location visualization
   - Medication adherence for that day
   - Photos attached (if any)
   - Edit/Delete options
7. Export Section:
   - "Export to PDF" button -"Share with Doctor" button

Style: Split-screen showing both timeline and calendar views. Use animations to demonstrate filtering. Include data visualization examples with charts and graphs.`
  },
  {
    id: 4,
    title: 'Exporting Data for Your Doctor', description: 'Generate professional health summaries for medical appointments. This tutorial shows how to select date ranges, customize report content, preview your summary, and export as PDF or send directly via email. Learn to include symptom trends, medication logs, and key insights for effective doctor communication.', duration: '4:20', category: 'Export & Sharing',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf1dfb00-1766574913969.png",
    videoUrl: '#',
    prompt: `Create a 4-minute tutorial for "Doctor Summary Export" in MyBodyToday. Demonstrate: 1. Navigate to"Doctor Summary Export" from main menu
2. Date Range Selector:
   - Quick options: Last Visit (auto-detects), Last Month, Last 3 Months, Custom
   - User selects "Last 3 Months"
3. Report Customization:
   - Checkboxes to include:
     ✓ Symptom Timeline
     ✓ Severity Trends Graph
     ✓ Most Frequent Symptoms
     ✓ Trigger Analysis
     ✓ Medication Adherence Log
     ✓ Body Location Heatmap
     ✓ Daily Impact Summary
   - User selects all options
4. Quick Stats Preview:
   - Cards showing: Total Entries (45), Average Severity (5.2/10), Most Common Symptom (Headache - 18 times), Medication Adherence (87%)
5. Summary Preview:
   - Scrollable preview of generated PDF
   - Professional medical report format with:
     * Patient info header
     * Executive summary
     * Detailed symptom breakdown
     * Visual charts and graphs
     * Medication log table
     * Notes section
6. Export Controls:
   - "Download PDF" button (downloads to device)
   - "Email to Doctor" button:
     * Modal appears with doctor's email field
     * Optional password protection toggle
     * Custom message field
     * "Send" button -"Print" button for physical copy
7. Success Confirmation:
   - "Report sent successfully to dr.smith@clinic.com"
   - Option to save doctor's email for future exports

Style: Professional, medical-grade presentation. Show actual PDF preview with realistic health data. Use smooth transitions between customization and preview. Include voiceover explaining how this helps doctor appointments.`
  },
  {
    id: 5,
    title: 'Advanced Analytics & Pattern Recognition', description: 'Unlock Premium features including correlation analysis, trigger pattern detection, seasonal trend identification, predictive insights, and intervention effectiveness tracking. Learn how AI-powered analytics help you understand what affects your symptoms and optimize your health management strategies.', duration: '10:30', category: 'Premium Features',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1208d72d4-1768534439777.png",
    videoUrl: '#',
    prompt: `Create a 10-minute advanced tutorial for "Premium Analytics" in MyBodyToday. Show:

1. Premium Badge:
   - "Upgrade to Premium" banner for Basic users
   - Premium users see full analytics dashboard
2. Correlation Matrix:
   - Heatmap showing relationships between:
     * Symptoms vs. Triggers (e.g., Stress → Headache: 85% correlation)
     * Symptoms vs. Weather (e.g., Rain → Joint Pain: 72%)
     * Symptoms vs. Medications (e.g., Ibuprofen → Pain Reduction: 68%)
   - Interactive: tap cell to see detailed breakdown
3. Pattern Recognition Panel:
   - "Weekly Patterns" graph: symptoms by day of week -"Time of Day"analysis: morning vs. evening symptom frequency -"Symptom Clusters": groups of symptoms that occur together
4. Trigger Analysis:
   - Bar chart of most common triggers
   - Timeline showing trigger → symptom onset delay
   - Recommendations: "Avoid caffeine after 2 PM to reduce evening headaches"
5. Seasonal Correlations:
   - Year-over-year comparison graph
   - Seasonal trend indicators (Spring allergies, Winter joint pain)
   - Weather impact analysis with temperature/humidity overlays
6. Predictive Insights:
   - AI-powered predictions: "Based on your patterns, you're 70% likely to experience a headache tomorrow"
   - Risk factors highlighted
   - Preventive suggestions
7. Intervention Effectiveness:
   - Before/After comparison when starting new medication
   - Lifestyle change impact tracking
   - Success rate metrics
8. Exportable Report:
   - "Generate Advanced Analytics Report" button
   - Comprehensive PDF with all insights
9. Analytics Filters:
   - Date range, symptom type, severity threshold
   - Real-time chart updates

Style: Data-rich dashboard with interactive charts. Use animations to show correlations forming. Include AI "thinking" animations for predictive insights. Professional color scheme with data visualization best practices.`
  },
  {
    id: 6,
    title: 'Caregiver Dashboard Tutorial',
    description: 'Complete guide for caregivers managing multiple dependents. Learn to add family members or patients, set up custom alert thresholds, monitor real-time health status, view aggregated analytics across dependents, and receive notifications for critical symptom changes. Includes relationship management and privacy controls.',
    duration: '9:00',
    category: 'Caregiver Features',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1042d9fdb-1768656339477.png",
    videoUrl: '#',
    prompt: `Create a 9-minute comprehensive tutorial for "Caregiver Dashboard" in MyBodyToday. Demonstrate:1. Caregiver Role Selection:- During signup, select "Caregiver" user type- Caregiver dashboard unlocks2. Adding Dependents:- "Add Dependent" button
   - Form fields: Name, Relationship (Parent, Child, Spouse, Patient, Other), Age, Date of Birth
   - Optional: Link to existing MyBodyToday account OR create profile for them
   - User adds "Mom (Age 68)" and "Son (Age 12)"
3. Dependent User Cards:
   - Grid of cards, one per dependent
   - Each card shows:
     * Profile photo
     * Name and relationship
     * Last check-in time
     * Current status indicator (Green: Good, Yellow: Monitor, Red: Alert)
     * Quick stats: entries this week, average severity
   - Tap card to view detailed dashboard for that dependent
4. Alert Configuration:
   - "Alert Settings" for each dependent
   - Severity threshold slider: "Notify me when severity exceeds 7/10"
   - Impact category alerts: checkboxes for Work, Sleep, Mobility, etc.
   - Notification channels: Email, SMS, In-App
   - Quiet hours: "Don't send alerts between 10 PM - 7 AM"5. Real-Time Monitoring:- Activity Timeline showing all dependents' recent entries
   - Filter by dependent or view all
   - Real-time updates when dependent logs new symptom
6. Alert Notification Banner:
   - Red banner appears: "⚠️ Mom logged severe pain (9/10) - Lower Back"
   - Tap to view full entry details
   - "Acknowledge" button to mark as reviewed -"Contact" button to call/message dependent
7. Aggregated Analytics:
   - "Family Overview" tab
   - Comparison charts: severity trends for all dependents
   - Identify patterns across family members
8. Privacy Controls:
   - Each dependent can grant/revoke caregiver access
   - Caregiver sees only what dependent allows
   - "Request Access" button if dependent hasn't granted permission
9. Relationship Management:
   - Edit dependent profiles
   - Remove dependent (with confirmation)
   - Transfer caregiver role to another user

Style: Family-focused, warm color palette. Show multi-user interface with clear visual separation between dependents. Include notification animations. Use realistic family scenarios (elderly parent, child with chronic condition).`
  },
  {
    id: 7,
    title: 'Managing Your Subscription', description: 'Navigate subscription plans (Basic, Premium, Family), understand feature limits and usage tracking, upgrade or downgrade plans, manage billing information through Stripe portal, view payment history, and understand the benefits of each tier. Learn about usage limits for exports, analytics, and storage.', duration: '5:00', category: 'Subscription',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_11492703a-1765758352926.png",
    videoUrl: '#',
    prompt: `Create a 5-minute tutorial for "Subscription Management" in MyBodyToday. Show: 1. Navigate to"Subscription Management" from Settings
2. Current Plan Card:
   - Badge: "Basic Plan" (or Premium/Family)
   - Price: $0/month (Basic), $9.99/month (Premium), $19.99/month (Family)
   - Renewal date
   - "Manage Subscription" button
3. Plan Comparison Table:
   - Three columns: Basic, Premium, Family
   - Features comparison:
     * Basic: 10 exports/month, 30 days history, 1 user
     * Premium: Unlimited exports, Unlimited history, Advanced analytics, 1 user
     * Family: All Premium features + 5 dependents, Caregiver dashboard
   - "Upgrade" buttons for higher tiers
4. Usage Tracking:
   - Progress bars showing current usage:
     * Exports: 7/10 used this month
     * Storage: 45 MB / 100 MB
     * Dependents: 2/5 (Family plan only)
   - "Resets in 12 days" countdown
5. Upgrade Flow:
   - User clicks "Upgrade to Premium" - Modal:"Choose Billing Interval" - Monthly ($9.99) or Yearly ($99.99 - Save 17%)
   - Stripe checkout form:
     * Card number, expiry, CVC
     * Billing address
     * "Subscribe" button - Success:"Welcome to Premium! 🎉"
6. Billing History:
   - Table of past invoices:
     * Date, Amount, Status (Paid/Failed), Download PDF
   - "View All Invoices" link to Stripe portal
7. Manage Billing:
   - "Update Payment Method" button → Stripe portal -"Cancel Subscription"button: * Warning modal:"You'll lose Premium features at end of billing period"* "Keep Subscription" or "Confirm Cancellation"8. Downgrade/Cancel:- If cancelled: "Your Premium access ends on [date]. Resubscribe anytime."- Downgrade to Basic: features locked with "Upgrade" prompts

Style: Professional, trust-building design. Show Stripe branding for payment security. Use clear pricing tables. Include testimonials: "Premium analytics helped me identify my migraine triggers!" - Sarah M.`
  },
  {
    id: 8,
    title: 'Privacy & Security Best Practices',
    description: 'Protect your health data with automatic AES-256 encryption, two-factor authentication (2FA) setup using authenticator apps or SMS, session management across devices, audit trail monitoring, data export and deletion controls, and understanding privacy-focused security features. Learn best practices for sharing data safely with caregivers and healthcare providers.',
    duration: '7:15',
    category: 'Security',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_16b0f1a88-1768816210175.png",
    videoUrl: '#',
    prompt: `Create a 7-minute tutorial for "Privacy & Security" in MyBodyToday. Demonstrate:1. Navigate to "Settings & Privacy"2. Encryption Management:- Display "Always Active" status for automatic encryption- Explanation: "Your symptom data is automatically encrypted with AES-256 before storage. Encryption is mandatory for all health data."- Show encryption statistics and algorithm details3. Two-Factor Authentication (2FA):- "Enable 2FA" button
   - Method selection:
     * Authenticator App (Google Authenticator, Authy) - Recommended
     * SMS Code
   - Authenticator App setup:
     * QR code displayed
     * User scans with authenticator app
     * Enter 6-digit code to verify
     * Backup codes generated (download and save securely)
   - Success: "2FA Enabled ✓"4. Session Management:- "Active Sessions" section
   - List of devices:
     * iPhone 13 - Los Angeles, CA - Active now
     * Windows PC - Los Angeles, CA - 2 hours ago
     * iPad - San Francisco, CA - 3 days ago
   - "Revoke" button for each session
   - "Revoke All Other Sessions" button5. Audit Trail:- "View Audit Trail" button (Officer access only - show locked state for regular users)
   - For officers: Timeline of all data access/modifications
     * Who accessed, what was changed, when, from which device
6. Privacy Controls:
   - "Symptom Data Visibility" dropdown:
     * Private (Only Me)
     * Caregivers Only
     * Public (for research - anonymized)
   - "Location Tracking" toggle (for weather correlation)
   - "Third-Party Sharing" toggle (anonymized data for medical research)
7. Data Export & Deletion:
   - "Export All My Data" button → Downloads encrypted JSON file- "Delete My Account" button:* Warning modal: "This action is permanent and cannot be undone"* Confirmation: Type "DELETE" to confirm* All data permanently erased8. Privacy-Focused Security:- "Privacy-focused with encrypted health data storage" badge
   - Link to Privacy Policy and Security Information
9. Best Practices Checklist:
   - ✓ Use strong, unique password
   - ✓ Enable 2FA
   - ✓ Enable encryption
   - ✓ Review active sessions monthly
   - ✓ Only share with trusted caregivers
   - ✓ Log out on shared devices

Style: Security-focused, professional design. Use lock icons, shield badges, and green checkmarks. Show real security indicators (SSL, encryption icons). Include reassuring voiceover about data protection. Use dark mode for security settings to convey seriousness.`
  }];



  const filteredTutorials =
  selectedCategory === 'all' ?
  tutorials :
  tutorials?.filter((t) => t?.category === selectedCategory);

  const categories = [...new Set(tutorials?.map((t) => t?.category))];

  if (!isOpen) return null;

  return (
    <SwipeableModal
      isOpen={isOpen}
      onClose={onClose}
      title="Video Tutorials"
      maxWidth="max-w-5xl"
      showCloseButton={true}>
      
      {/* Category Filter */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] touch-manipulation active:scale-95 ${
            selectedCategory === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
            }>
            
            All Videos
          </button>
          {categories?.map((category) =>
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] touch-manipulation active:scale-95 ${
            selectedCategory === category ?
            'bg-primary text-primary-foreground' :
            'bg-muted text-foreground hover:bg-muted/80'}`
            }>
            
              {category}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">How to Use These Prompts</h3>
              <p className="text-xs text-blue-700 mb-2">
                Copy any prompt below and use it with AI video generation tools like:
              </p>
              <ul className="text-xs text-blue-700 space-y-1 ml-4">
                <li>• <strong>Runway Gen-3</strong> - Advanced AI video generation</li>
                <li>• <strong>Synthesia</strong> - AI avatar-based video creation</li>
                <li>• <strong>Pictory</strong> - Script-to-video conversion</li>
                <li>• <strong>Descript</strong> - Screen recording with AI editing</li>
                <li>• <strong>Loom + AI</strong> - Screen capture with AI enhancements</li>
              </ul>
            </div>
          </div>
        </div>

        {filteredTutorials?.length === 0 ?
        <div className="text-center text-muted-foreground">No videos found for this category.</div> :

        <div className="grid grid-cols-1 gap-4">
            {filteredTutorials?.map((tutorial) =>
          <div
            key={tutorial?.id}
            className="group block bg-muted/50 rounded-lg overflow-hidden border border-border hover:border-primary/30 hover:shadow-therapeutic transition-smooth">
                
                      {/* Thumbnail */}
                      <div className="relative aspect-video bg-muted">
                        <img
                src={tutorial?.thumbnail}
                alt={tutorial?.title}
                className="w-full h-full object-cover" />
                  
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <div className="text-center px-4">
                            <Icon name="FileText" size={32} color="white" className="mx-auto mb-2" />
                            <p className="text-white text-sm font-medium">AI Video Prompt Available</p>
                          </div>
                        </div>
                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded text-xs text-white font-medium">
                          {tutorial?.duration}
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground mb-2">
                          {tutorial?.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                          {tutorial?.description}
                        </p>
                        
                        {/* Prompt Display */}
                        <div className="bg-background rounded-lg border border-border p-3 mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-foreground">AI Video Generation Prompt:</span>
                            <button
                    onClick={() => {
                      navigator.clipboard?.writeText(tutorial?.prompt);
                      // Could add toast notification here
                    }}
                    className="text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-1">
                        
                              <Icon name="Copy" size={14} />
                              Copy
                            </button>
                          </div>
                          <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto font-mono bg-muted/30 p-2 rounded">
                            {tutorial?.prompt}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                  onClick={() => {
                    navigator.clipboard?.writeText(tutorial?.prompt);
                  }}
                  className="flex-1 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-xs font-medium flex items-center justify-center gap-2">
                      
                            <Icon name="Copy" size={14} />
                            Copy Full Prompt
                          </button>
                        </div>
                      </div>
                    </div>
          )}
            </div>
        }
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-6 border-t border-border">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Lightbulb" size={16} />
          <span>Use these prompts with AI video tools to create tutorials</span>
        </div>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
          
          Close
        </button>
      </div>
    </SwipeableModal>);

};

export default VideoTutorialsModal;